import { dbf } from './dbf'
import { read as readZip } from '../file/zip';
import { POINT, LINESTRING, POLYGON, MULTI_POINT } from './geometry_type';

const ZIP = 'zip';
const SHP = 'shp';
const SHX = 'shx';
const DBF = 'dbf';
const PRJ = 'prj';

const SHP_MAGIC_VALUE = 9994;

// see https://github.com/itinero/nettopologysuite.io.portableshape/blob/master/NetTopologySuite.IO.PortableShape/ShapeGeometryType.cs
// see http://www.esri.com/library/whitepapers/pdfs/shapefile.pdf
const SHAPE_GEOMETRY_TYPE = {
	// NULL_SHAPE
   [0]: null,
   // POINT
   [1]: POINT,
   // POLY_LINE
   [3]: LINESTRING,
   //POLYGON
   [5]: POLYGON,
   //MULTI_POINT
   [8]: MULTI_POINT,
   // GEOTOOLS PointZ
   [9]: POINT,
   // GEOTOOLS LineStringZ 
   [10]: LINESTRING,   
   // POINT_Z
   [11]: POINT,
   // PolyLineMZ
   [13]: LINESTRING,
   // PolygonMZ
   [15]: POLYGON,
   // MULTI_POINT_Z
   [18]: MULTI_POINT,
   // GEOTOOLS PolygonZ
   [19]: POLYGON,
   // GEOTOOLS MultiPointZ 
   [20]: MULTI_POINT, 
   // POINT_M
   [21]: POINT,
   // POLY_LINE_M
   [23]: LINESTRING,
   // POLYGON_M
   [25]: POLYGON,
   // MULTI_POINT_M,
   [28]: MULTI_POINT,
   // MULTI_PATCH
   [31]: null
}

const getShapeGeometryType = ( type ) => {
    if( SHAPE_GEOMETRY_TYPE.hasOwnProperty(type) ) {
    	return SHAPE_GEOMETRY_TYPE[type]
    } else {
    	console.warn("Unknown geometry type: ", type);
    	return null;
    }
}	

class ShapefileFormat {
	
	constructor(options) {
		this.options = options;
	}
	
	getShp() {
		const { shp } = this.options;
		return shp;
	}
	
	getShx() {
		const { shx } = this.options;
		return shx;
	}	
	
	getDbf() {
		const { dbf } = this.options;
		return dbf;
	}
	
	getPrj() {
		const { prj } = this.options;
		return prj;
	}
		
	hasMultipleShapefiles() {
		const { multipleShapefiles } = this.options;
		return multipleShapefiles;
	}	
	
	isValidShp() {
		const { validShp } = this.options;
		return validShp;
	}
	
	isValidShx() {
		const { validShx } = this.options;
		return validShx;
	}
	
	getGeometryType() {
		const { geometryType } = this.options;
		return geometryType;
	}
	
	getFields() {
		const { fields } = this.options;
		return fields;
	}
}

class ZipEntryReader {
	constructor( object ) {
		this.object = object;
	}
	
	read(start, end) {
		let buffer = this.object.async('arraybuffer');

		if( start !== undefined && end !== undefined ) {	
			buffer = buffer.then(input => input.slice(start, end))
		}	
	
		return buffer;	
	}
	
	get name() {
		return this.object.name;
	}
} 

const checkExtension = (source, extension ) => {
	return source.toLowerCase() === extension;
}

const getName = (reader) => {
	const file_name = reader.name;
	const index = file_name.lastIndexOf(".");
	const name = file_name.substring(0, index);
	const extension = file_name.substring(index + 1, file_name.length).toLowerCase();
	
	return { name: name, extension: extension };
}

const getFileName = (reader) => {
	if( reader ) {
		const { name } = getName(reader);
		return name;
	}
}

const getZipReader = object => {
	if( object ) {
		return new ZipEntryReader(object);
	}
}

const checkZipShapefile =  async (reader) => {
	const zip = await readZip( reader );	   
	
	const objects = [];    
	zip.forEach((path, object) => objects.push(object));
	
	let shpFile = null;
	let shxFile = null;
	let dbfFile = null;
	let prjFile = null;
	
	let baseName = null;
	
	let multipleShapefiles = false;
	
	for( let entry of objects ) {
		const { name, extension } = getName( entry );

		const shp = checkExtension(extension, SHP);
		const shx = !shp && checkExtension(extension, SHX);
		const dbf = !shp && !shx && checkExtension(extension, DBF);
		const prj = !shp && !shx && !dbf && checkExtension(extension, PRJ);
		
		if( shp ) {
			shpFile = entry;
		} else if( shx ) {
			shxFile = entry;
		} else if( dbf ) {
			dbfFile = entry;
		} else if( prj ){
			prjFile = entry;
		}
		
		if( shp || shx || dbf || prj ) {
			if( baseName == null ) {
				baseName = name;
			} else {
				multipleShapefiles = multipleShapefiles || baseName !== name; 	
			}
		}		
	}	
	
	// more then one shapefile in zip
	if( multipleShapefiles ) {
		shpFile = null;
		shxFile = null;
	 	dbfFile = null;
	 	prjFile = null;
	}	
	
	const formatInfo = await readShapefile( getZipReader(shpFile), getZipReader(shxFile), getZipReader(dbfFile), getZipReader(prjFile) );
	formatInfo.options.multipleShapefiles = multipleShapefiles;
	
	return formatInfo;
}

const isValidFile = async (reader) => {
	const buffer = await reader.read(0, 8)
	const dataView = new DataView(buffer);
	return dataView.getUint32(0) == SHP_MAGIC_VALUE;
}

const getGeometryType = async (reader) => {
	const buffer = await reader.read(32, 36);
	const dataView = new DataView(buffer);
	return getShapeGeometryType( dataView.getUint32(0, true) );	
}

const readShapefile = async (shpFile, shxFile, dbfFile, prjFile ) => {
	const validShp = shpFile && await isValidFile( shpFile );
	const validShx = shxFile && await isValidFile( shxFile ); 
	
	const geometryType = validShp && await getGeometryType( shpFile );
	const fields = dbfFile && await dbf( dbfFile );
		
	const options = {
		shp: getFileName(shpFile),
		shx: getFileName(shxFile),
		dbf: getFileName(dbfFile),
		prj: getFileName(prjFile),
		validShp: !!validShp,
		validShx: !!validShx,
		geometryType: geometryType,
		fields: fields
	}
	
	return new ShapefileFormat( options );
}

const shapefile = async (shpOrZipfile, shxFile, dbfFile, prjFile) => {	
	const { extension } = getName( shpOrZipfile );

	if( checkExtension(extension, ZIP) ) {
		return checkZipShapefile( shpOrZipfile);
	} else {
		return readShapefile( shpOrZipfile, shxFile, dbfFile, prjFile );
	}
}

export {
	shapefile
}